import { Dialog, IconButton } from '@mui/material';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import StraiCoinIcon from '../../../../Theme/icons/straicoin';
import useThemeStore from 'Theme/store';

const CostInfoModal = ({
  open,
  onClose,
  costValue,
  onActivateSmartPricing,
  onDeactivateSmartPricing,
  currentPricingMethod,
}) => {
  const { theme: themeValue } = useThemeStore();
  const navigate = useNavigate();
  const isSmartPricingActive = currentPricingMethod === 'perMessage';

  // Determine the cost level for content customization (for perWord mode)
  const getCostLevel = (cost) => {
    if (cost < 60) return 'low';
    if (cost < 200) return 'moderate';
    return 'high';
  };

  const costLevel = getCostLevel(costValue);

  // Customize message based on cost level (for perWord mode)
  const getCostMessage = () => {
    const costRanges = {
      low: {
        color: 'text-[#9cb773]',
        range: 'under 60 coins',
        text: 'low',
      },
      moderate: {
        color: 'text-[#e3c06f]',
        range: '60-199 coins',
        text: 'moderate',
      },
      high: {
        color: 'text-[#e68e5b]',
        range: '200+ coins',
        text: 'high',
      },
    };

    const { color, range, text } = costRanges[costLevel];

    switch (costLevel) {
      case 'low':
        return `This prompt has a <span class="${color} font-semibold">${text} cost</span> (${range}) and is efficient for your coin usage.`;
      case 'moderate':
        return `
          <p>This prompt has a <span class="${color} font-semibold">${text} cost</span> (${range}).</p>
          <p class="mt-2">This message will use a bit more coins than usual due to:</p>
          <ul class="list-disc ml-5 mt-2">
            <li>The conversation length is growing</li>
            <li>The selected AI model's coin usage rate</li>
            <li>Attached files in the context</li>
          </ul>
        `;
      case 'high':
        return `
          <p>This prompt has a <span class="${color} font-semibold">${text} cost</span> (${range}).</p>
          <p class="mt-2">This message will use significantly more coins than usual due to:</p>
          <ul class="list-disc ml-5 mt-2">
            <li>The conversation has become quite extensive</li>
            <li>The selected AI model's high coin usage rate</li>
            <li>Large files in the context</li>
          </ul>
        `;
      default:
        return 'Your prompt cost may vary based on its length and complexity.';
    }
  };

  const handleTogglePricing = async () => {
    if (isSmartPricingActive) {
      if (onDeactivateSmartPricing) {
        const success = await onDeactivateSmartPricing();
        if (success) onClose();
      }
    } else {
      if (onActivateSmartPricing) {
        const success = await onActivateSmartPricing();
        if (success) onClose();
      }
    }
  };

  return (
    <Dialog
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '21px',
        },
      }}
      open={open}
      onClose={onClose}
    >
      <div className="font-figtree h-full w-full relative text-[#211E21] dark:text-seasalt sm:max-w-lg px-[30px] py-7 flex flex-col gap-5">
        {/* Close button */}
        <div className="absolute top-[6px] right-[6px] group">
          <IconButton onClick={onClose}>
            <XMarkIcon className="h-3 w-3 cursor-pointer text-[#211E21] dark:text-seasalt dark:group-hover:text-quicksilver group-hover:text-battleship-gray" />
          </IconButton>
        </div>

        {/* Title */}
        <div className="w-full flex justify-center">
          <div className="text-[#211E21] dark:text-crystal-bell text-[28px] leading-7 font-bold text-center">
            {isSmartPricingActive
              ? 'Pricing per Message'
              : 'Prompt Cost Information'}
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-auto">
          <div className="flex justify-center mb-4 p-3">
            <div
              className={`
      flex flex-row items-center px-3 py-1 rounded-[20px] font-semibold
      ${
        isSmartPricingActive
          ? 'border border-[#f2a474] bg-transparent neon-effect-custom border-neon-custom'
          : costLevel === 'low'
          ? 'bg-[#9cb773]'
          : costLevel === 'moderate'
          ? 'bg-[#e3c06f]'
          : 'bg-[#e68e5b]'
      }
    `}
            >
              <StraiCoinIcon
                className={`w-6 h-6 ${
                  isSmartPricingActive ? 'text-[#f2a474]' : 'text-white'
                }`}
              />
              <span
                className={`mx-1 ${
                  isSmartPricingActive ? 'text-[#f2a474]' : 'text-white'
                } text-xl`}
              >
                {costValue}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {isSmartPricingActive ? (
              <>
                <div className="bg-[#F2A474]/10 dark:bg-[#F2A474]/30 border border-[#F2A474]/20 dark:border-[#F2A474]/40 p-4 rounded-lg">
                  <h3 className="font-semibold text-[#F2A474] dark:text-[#F2A474]/80 mb-2 text-base">
                    Pricing per Message Active
                  </h3>
                  <p className="text-sm">
                    Every interaction deducts coins based on the length of your
                    prompt and response, but with an upper cap. This ensures
                    predictable pricing, even in long conversations where word
                    accumulation previously caused costs to escalate
                    dramatically.
                  </p>
                </div>

                <div
                  className={`${
                    themeValue === 'dark' ? 'bg-[#252425]' : 'bg-[#f8f8f8]'
                  } p-4 rounded-lg shadow-sm`}
                >
                  <h3 className="font-semibold mb-2 text-[#211E21] dark:text-seasalt text-base">
                    Benefits of Pricing per Message
                  </h3>
                  <ul className="list-disc ml-5 text-[#211E21] dark:text-seasalt text-sm">
                    <li>Predictable maximum cost for every prompt</li>
                    <li>
                      No sharp increases in coin usage for accumulating context
                    </li>
                    <li>
                      Economical for extended conversations and large attached
                      files
                    </li>
                    <li>Optimized context management</li>
                  </ul>
                </div>

                <p className="italic text-center text-xs mt-2">
                  You can switch back to standard word-based pricing if you
                  prefer.
                </p>
              </>
            ) : (
              // Standard Pricing (perWord) Content
              <>
                <div
                  className={`${
                    themeValue === 'dark' ? 'bg-[#252425]' : 'bg-[#f8f8f8]'
                  } p-4 rounded-lg shadow-sm`}
                >
                  <div
                    className="text-[#211E21] dark:text-seasalt"
                    dangerouslySetInnerHTML={{ __html: getCostMessage() }}
                  />
                </div>

                <p className="text-center mt-4">
                  If you want to have <strong>coin-efficient prompts</strong>,
                  you could consider activating{' '}
                  <strong>Pricing per Message</strong>:
                </p>
              </>
            )}
          </div>
        </div>
        <div className="text-center text-xs">
          <p>
            Want to learn more about pricing methods?{' '}
            <a
              href="/user-settings#pricing-settings"
              className="text-violet-blue cursor-pointer hover:underline"
              onClick={(e) => {
                e.preventDefault();
                onClose();
                navigate('/user-settings#pricing-settings');
                // Add a small delay to ensure the navigation completes before scrolling
                setTimeout(() => {
                  const element = document.getElementById('pricing-settings');
                  if (element) {
                    element.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                  }
                }, 100);
              }}
            >
              Visit pricing settings
            </a>
            {' or '}
            <a
              href="https://straico.com/pricing-modes/"
              className="text-violet-blue cursor-pointer hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              check our detailed guide
            </a>
          </p>
        </div>

        <div className="flex w-full items-center justify-center">
          <button
            className={`flex items-center justify-center text-[18px] leading-6 font-semibold rounded-xl px-5 py-2 text-white focus:outline-none ${
              isSmartPricingActive
                ? 'bg-[#F2A474] hover:bg-[#F2A474]/80'
                : 'bg-[#464bba] hover:bg-[#464bba]/80'
            }`}
            onClick={handleTogglePricing}
          >
            {isSmartPricingActive
              ? 'Switch to Standard Pricing'
              : 'Activate Pricing per Message'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CostInfoModal;
